import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";

/**
 * Same contract as the generated `requireSupabaseAuth`, but the Supabase SDK is
 * imported inside the handler so it never reaches the client bundle graph.
 */
export const requireSupabaseAuth = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const SUPABASE_URL = process.env["SUPABASE_URL"];
    const SUPABASE_PUBLISHABLE_KEY = process.env["SUPABASE_PUBLISHABLE_KEY"];

    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      const missing = [
        ...(!SUPABASE_URL ? ["SUPABASE_URL"] : []),
        ...(!SUPABASE_PUBLISHABLE_KEY ? ["SUPABASE_PUBLISHABLE_KEY"] : []),
      ];
      throw new Error(
        `Missing Supabase environment variable(s): ${missing.join(", ")}. Connect Supabase in Lovable Cloud.`,
      );
    }

    const request = getRequest();
    if (!request?.headers) throw new Error("Unauthorized: No request headers available");

    const authHeader = request.headers.get("authorization");
    if (!authHeader) throw new Error("Unauthorized: No authorization header provided");
    if (!authHeader.startsWith("Bearer "))
      throw new Error("Unauthorized: Only Bearer tokens are supported");

    const token = authHeader.replace("Bearer ", "");
    if (!token) throw new Error("Unauthorized: No token provided");
    if (token.split(".").length !== 3) throw new Error("Unauthorized: Invalid token");

    const { createClient } = await import("@supabase/supabase-js");
    const isOpaqueKey =
      SUPABASE_PUBLISHABLE_KEY.startsWith("sb_publishable_") ||
      SUPABASE_PUBLISHABLE_KEY.startsWith("sb_secret_");

    const supabase = createClient<import("@/integrations/supabase/types").Database>(
      SUPABASE_URL,
      SUPABASE_PUBLISHABLE_KEY,
      {
        global: {
          fetch: (input, init) => {
            const headers = new Headers(
              typeof Request !== "undefined" && input instanceof Request
                ? input.headers
                : undefined,
            );
            if (init?.headers) {
              new Headers(init.headers).forEach((value, key) => headers.set(key, value));
            }
            if (isOpaqueKey && headers.get("Authorization") === `Bearer ${SUPABASE_PUBLISHABLE_KEY}`) {
              headers.delete("Authorization");
            }
            headers.set("apikey", SUPABASE_PUBLISHABLE_KEY);
            return fetch(input, { ...init, headers });
          },
          headers: { Authorization: `Bearer ${token}` },
        },
        auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
      },
    );

    const { data, error } = await supabase.auth.getClaims(token);
    if (error || !data?.claims) throw new Error("Unauthorized: Invalid token");
    if (!data.claims.sub) throw new Error("Unauthorized: No user ID found in token");

    return next({ context: { supabase, userId: data.claims.sub, claims: data.claims } });
  },
);
